import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { WhatsappLogo, Check } from "@phosphor-icons/react";

/**
 * Compact WhatsApp opt-in banner for the candidate portal.
 * Renders a single toggleable card that hits GET/POST /me/candidate/whatsapp-opt-in.
 *
 * Design goals:
 *   - Non-intrusive (dismissed state hides the card until re-enabled from settings)
 *   - Transparency: shows current phone + STOP-instructions
 *   - Zero disruption: opting out never fails silently — always confirmed with a toast
 */
export default function WhatsAppOptInCard() {
    const [state, setState] = useState(null); // {opt_in, phone, opt_out_at}
    const [busy, setBusy] = useState(false);
    const [phoneInput, setPhoneInput] = useState("");
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        let alive = true;
        api.get("/me/candidate/whatsapp-opt-in")
            .then((r) => { if (alive) { setState(r.data); setPhoneInput(r.data?.phone || ""); } })
            .catch(() => { /* candidate not linked → nothing to do */ });
        return () => { alive = false; };
    }, []);

    if (!state) return null;

    const optedIn = state.opt_in;

    const save = async (opt_in) => {
        setBusy(true);
        try {
            const body = { opt_in };
            if (opt_in && phoneInput.trim()) body.phone = phoneInput.trim();
            const r = await api.post("/me/candidate/whatsapp-opt-in", body);
            setState((s) => ({ ...(s || {}), opt_in: r.data.opt_in }));
            toast.success(
                opt_in
                    ? "You'll now get WhatsApp reminders. Reply STOP anytime to opt out."
                    : "WhatsApp reminders turned off.",
            );
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Couldn't update preference.");
        } finally {
            setBusy(false);
        }
    };

    return (
        <div
            data-testid="whatsapp-optin-card"
            className={
                optedIn
                    ? "border border-[#25D366]/30 bg-[#25D366]/5 p-4 mb-6"
                    : "border border-[#E5E5E5] bg-white p-4 mb-6"
            }
        >
            <div className="flex items-start gap-3">
                <div
                    className={`shrink-0 w-9 h-9 flex items-center justify-center ${
                        optedIn ? "bg-[#25D366] text-white" : "bg-[#F5F5F5] text-[#525252]"
                    }`}
                >
                    <WhatsappLogo size={18} weight="fill" />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="text-[10px] font-black uppercase tracking-[0.2em] text-[#525252] mb-0.5">
                        WhatsApp reminders
                    </p>
                    <p className="text-sm text-ink font-bold">
                        {optedIn
                            ? "Interview reminders will be sent to WhatsApp."
                            : "Never miss an interview — turn on WhatsApp reminders."}
                    </p>
                    {!expanded && !optedIn && (
                        <button
                            type="button"
                            data-testid="whatsapp-optin-enable"
                            onClick={() => setExpanded(true)}
                            className="text-xs font-bold text-cobalt hover:underline mt-1"
                        >
                            Enable WhatsApp reminders →
                        </button>
                    )}
                    {expanded && !optedIn && (
                        <div className="mt-3 space-y-2">
                            <div>
                                <label className="text-[10px] font-black uppercase tracking-wider text-[#525252] block mb-1">
                                    Your WhatsApp number
                                </label>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    data-testid="whatsapp-optin-phone"
                                    value={phoneInput}
                                    onChange={(e) => setPhoneInput(e.target.value)}
                                    placeholder="+91 98765 43210"
                                    className="w-full sm:w-64 border border-[#E5E5E5] px-3 py-2 text-sm focus:outline-none focus:border-cobalt"
                                />
                                <p className="text-[10px] text-[#A3A3A3] mt-1">
                                    We&apos;ll send interview + registration reminders only. Reply STOP anytime.
                                </p>
                            </div>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="whatsapp-optin-confirm"
                                    onClick={() => save(true)}
                                    disabled={busy || !phoneInput.trim()}
                                    className="btn-gradient px-4 py-2 text-xs font-black uppercase tracking-wider inline-flex items-center gap-1.5 disabled:opacity-50"
                                >
                                    <Check size={12} weight="bold" /> Opt in
                                </button>
                                <button
                                    type="button"
                                    onClick={() => setExpanded(false)}
                                    disabled={busy}
                                    className="text-xs font-bold uppercase tracking-wider text-[#525252] hover:text-ink px-2"
                                >
                                    Cancel
                                </button>
                            </div>
                        </div>
                    )}
                    {optedIn && (
                        <div className="mt-2 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4 text-xs text-[#525252]">
                            <span className="font-mono">
                                {state.phone ? `+${String(state.phone).replace(/^\+/, "")}` : "no phone on file"}
                            </span>
                            <button
                                type="button"
                                data-testid="whatsapp-optin-disable"
                                onClick={() => save(false)}
                                disabled={busy}
                                className="text-xs font-bold text-[#DC2626] hover:underline"
                            >
                                Turn off reminders
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
